import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import MatchCard from "@/components/base/MatchCard";
import ReplayCard from "@/components/base/ReplayCard";
import AnalysisCard from "@/components/base/AnalysisCard";
import { matches } from "@/mocks/matches";
import { replays } from "@/mocks/replays";
import { analyses } from "@/mocks/analyses";
import { usePageMeta } from "@/hooks/usePageMeta";

function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent-200 text-foreground-950 rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

const tabs = [
  { key: "all", label: "全部" },
  { key: "match", label: "比赛" },
  { key: "replay", label: "回放" },
  { key: "analysis", label: "解析" },
] as const;

type TabKey = (typeof tabs)[number]["key"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [input, setInput] = useState(params.get("q") ?? "");
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [tab, setTab] = useState<TabKey>("all");

  usePageMeta(
    "搜索足球赛事与回放 - 51网站在线观看免费播播足球",
    "搜索51足球直播站内的足球赛事、精彩回放与赛事解析文章，支持按球队、联赛、比赛名称快速查找。",
  );

  const q = query.trim().toLowerCase();

  const matchResults = useMemo(
    () =>
      q
        ? matches.filter((m) =>
            [m.leagueName, m.home.name, m.away.name, m.home.short, m.away.short, m.round, m.venue]
              .join(" ")
              .toLowerCase()
              .includes(q),
          )
        : [],
    [q],
  );

  const replayResults = useMemo(
    () =>
      q
        ? replays.filter((r) =>
            [r.title, r.leagueName, r.home, r.away].join(" ").toLowerCase().includes(q),
          )
        : [],
    [q],
  );

  const analysisResults = useMemo(
    () =>
      q
        ? analyses.filter((a) =>
            [a.title, a.excerpt, a.tags.join(" ")].join(" ").toLowerCase().includes(q),
          )
        : [],
    [q],
  );

  const total = matchResults.length + replayResults.length + analysisResults.length;
  const hotMatches = matches.filter((m) => m.hot);

  const runSearch = () => {
    setQuery(input);
    const next = new URLSearchParams(params);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const counts: Record<TabKey, number> = {
    all: total,
    match: matchResults.length,
    replay: replayResults.length,
    analysis: analysisResults.length,
  };

  return (
    <SiteLayout>
      <PageHeader
        title="搜索"
        subtitle="输入球队、联赛或比赛名称，快速找到你想看的直播、回放与赛事解析。"
        crumbs={[{ label: "首页", to: "/" }, { label: "搜索" }]}
      />

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto">
          <div className="bg-background-50 border border-background-200 rounded-lg p-3 md:p-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") runSearch();
                }}
                placeholder="搜索球队 / 联赛 / 赛事名称，例如：曼城、欧冠"
                className="w-full h-12 pl-11 pr-4 rounded-md bg-background-100 border border-transparent focus:border-primary-300 focus:bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors"
              />
            </div>
            <button
              type="button"
              onClick={runSearch}
              className="h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>

          {q && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      tab === t.key
                        ? "bg-primary-500 text-white"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {t.label}
                    <span className="ml-1.5 text-xs opacity-70">{counts[t.key]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!q && (
            <div className="mt-10">
              <div className="py-12 text-center bg-background-100 rounded-lg border border-background-200">
                <i className="ri-search-eye-line text-4xl text-foreground-300" />
                <p className="mt-3 text-sm text-foreground-500">
                  输入关键词开始搜索，可搜索球队、联赛或赛事名称
                </p>
              </div>
              <div className="mt-10">
                <h2 className="text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-fire-line text-accent-500" />
                  热门赛事推荐
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                  {hotMatches.map((m) => (
                    <MatchCard key={m.id} match={m} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {q && total === 0 && (
            <div className="mt-10">
              <div className="py-12 text-center bg-background-100 rounded-lg border border-background-200">
                <i className="ri-emotion-unhappy-line text-4xl text-foreground-300" />
                <p className="mt-3 text-sm text-foreground-500">
                  没有找到与「{query}」相关的内容
                </p>
                <p className="mt-1 text-xs text-foreground-400">
                  试试更换关键词，或看看下面的热门赛事
                </p>
              </div>
              <div className="mt-10">
                <h2 className="text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-fire-line text-accent-500" />
                  热门赛事推荐
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                  {hotMatches.map((m) => (
                    <MatchCard key={m.id} match={m} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {q && total > 0 && (
            <div className="mt-8 flex flex-col gap-10">
              {(tab === "all" || tab === "match") && matchResults.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-4">
                    比赛结果 <span className="text-sm text-foreground-400">({matchResults.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                    {matchResults.map((m) => (
                      <MatchCard key={m.id} match={m} />
                    ))}
                  </div>
                </div>
              )}

              {(tab === "all" || tab === "replay") && replayResults.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-4">
                    回放视频 <span className="text-sm text-foreground-400">({replayResults.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {replayResults.map((r) => (
                      <ReplayCard key={r.id} replay={r} />
                    ))}
                  </div>
                </div>
              )}

              {(tab === "all" || tab === "analysis") && analysisResults.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-4">
                    赛事解析 <span className="text-sm text-foreground-400">({analysisResults.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 gap-4">
                    {analysisResults.map((a) => (
                      <AnalysisCard key={a.id} analysis={a} />
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs text-foreground-400 border-t border-background-200 pt-5">
                搜索关键词：
                <span className="text-foreground-700 font-medium">
                  <HighlightText text={query} query={query} />
                </span>
              </div>
            </div>
          )}

          {!q && (
            <div className="mt-10 flex flex-wrap gap-2">
              <span className="text-xs text-foreground-400 py-1.5">热门搜索：</span>
              {["曼城", "欧冠", "国家德比", "利物浦", "世界杯", "英超"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setInput(k);
                    setQuery(k);
                    const next = new URLSearchParams(params);
                    next.set("q", k);
                    setParams(next, { replace: true });
                  }}
                  className="px-3.5 py-1.5 rounded-full bg-background-100 hover:bg-primary-50 hover:text-primary-700 text-sm text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {k}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}